import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ArrowUpRight, Check, X, Calendar } from "lucide-react";
import { API_BASE, useAuth } from "@/context/AuthContext";

const MAX_ATTEMPTS = 6;
const POLL_MS = 2000;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking|paid|pending|failed|expired
  const [info, setInfo] = useState(null);
  const [schedulingUrl, setSchedulingUrl] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/config`);
        setSchedulingUrl(data.scheduling_url || null);
      } catch (_e) { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API_BASE}/payments/checkout/status/${sessionId}`, { withCredentials: true });
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= MAX_ATTEMPTS) { setStatus("pending"); return; }
        timer = setTimeout(poll, POLL_MS);
      } catch (_e) {
        setStatus("failed");
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen px-6 sm:px-10 py-16 flex flex-col">
      <div className="grain" />
      <div className="max-w-2xl mx-auto w-full glass p-12 my-auto">
        <div className="font-label text-[rgba(232,226,212,0.55)] mb-4">Checkout</div>
        {status === "checking" && <h1 className="font-display text-[#efe7d2] text-4xl sm:text-5xl leading-[1.05]">Confirming your <span className="font-italic-display">payment...</span></h1>}
        {status === "paid" && (
          <>
            <div className="w-12 h-12 rounded-full bg-[#d9b87d]/15 border border-[#d9b87d]/60 flex items-center justify-center mb-6"><Check size={20} className="text-[#d9b87d]" /></div>
            <h1 className="font-display text-[#efe7d2] text-4xl sm:text-5xl leading-[1.05]">Payment <span className="font-italic-display">received.</span></h1>
            <p className="mt-6 text-[rgba(232,226,212,0.75)] leading-relaxed">
              Thank you. Your consultation is confirmed. If this is your first purchase, an account was created for <span className="text-[#efe7d2]">{info?.user_email}</span> — sign in to access your client portal.
            </p>
            {schedulingUrl && (
              <div className="mt-8 p-6 border border-[#d9b87d]/40 bg-[#d9b87d]/5" data-testid="checkout-scheduling-card">
                <div className="font-label text-[#d9b87d] mb-2 flex items-center gap-2"><Calendar size={12} /> Schedule Your Call</div>
                <p className="text-[rgba(232,226,212,0.75)] text-sm mb-4">Pick a time that works — takes 30 seconds.</p>
                <a href={schedulingUrl} target="_blank" rel="noopener noreferrer" data-testid="checkout-schedule-link" className="btn-solid inline-flex">
                  Book Your Time Slot <ArrowUpRight size={14} />
                </a>
              </div>
            )}
            <p className="mt-6 text-[rgba(232,226,212,0.7)] text-sm leading-relaxed">
              <strong className="text-[#efe7d2]">Next:</strong> sign in, upload your materials at least 24 hours before the call, and after the call your notes, commentary, and analysis will be posted to your portal.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" className="btn-solid">Sign In <ArrowUpRight size={14} /></Link>
              <Link to="/dashboard" className="btn-outline">Go to Client Portal</Link>
              <Link to="/" className="btn-outline">Back to Site</Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="font-display text-[#efe7d2] text-4xl sm:text-5xl leading-[1.05]">Still <span className="font-italic-display">processing.</span></h1>
            <p className="mt-6 text-[rgba(232,226,212,0.75)] leading-relaxed">Stripe is still finalizing this payment. You can refresh in a minute or check your email for confirmation.</p>
            <button onClick={() => navigate(0)} className="btn-outline mt-8">Refresh</button>
          </>
        )}
        {(status === "expired" || status === "failed") && (
          <>
            <div className="w-12 h-12 rounded-full bg-[#ff6b6b]/10 border border-[#ff6b6b]/40 flex items-center justify-center mb-6"><X size={20} className="text-[#ff6b6b]" /></div>
            <h1 className="font-display text-[#efe7d2] text-4xl sm:text-5xl leading-[1.05]">Payment <span className="font-italic-display">not completed.</span></h1>
            <p className="mt-6 text-[rgba(232,226,212,0.75)] leading-relaxed">Your card was not charged. You can try again from the course page.</p>
            <Link to="/#services" className="btn-solid mt-8 inline-flex">Back to Courses <ArrowUpRight size={14} /></Link>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
